import { NewAmbulatoryRepository as IAmbulatoryRepository } from 'repository/interfaces/new-ambulatory-repository'
import { IApiRepository } from 'service/protocols/api/api-repository'
import { GetAmbulatoryCounters } from 'domain/usecases/ambulatory/get-ambulatory-counters'
import { GetAmbulatoryOrders } from 'domain/usecases/ambulatory/get-ambulatory-orders'
import parseParams from 'common/utils/parseParams'
import { SearchAmbulatoryOrders } from 'domain/usecases/ambulatory/search-ambulatory-order'
import handleGraphQLError from 'repository/graphql/utils/handle-error'
import { RepositoryErrors } from 'repository/errors/repository-errors'
import { ApiStatusCode } from 'service/protocols/api/api-client'
import { GetOneAmbulatoryOrder } from 'domain/usecases/ambulatory/get-one-ambulatory-order'
import { LoadAmbulatoryOrderDocument } from 'domain/usecases/ambulatory/load-ambulatory-order-document'
import { GetAmbulatoryProcedures } from 'domain/usecases/ambulatory/get-ambulatory-procedures'
import { GetAmbulatoryCids } from 'domain/usecases/ambulatory/get-ambulatory-cids'
import { GetAmbulatoryMedications } from 'domain/usecases/ambulatory/get-ambulatory-medications'
import { CustomError } from 'common/errors'
import { CreateAmbulatoryOrder } from 'domain/usecases/ambulatory/create-ambulatory-order'

export class NewAmbulatoryRepository implements IAmbulatoryRepository {
  constructor(private readonly apiRepository: IApiRepository) {}

  async getAmbulatoryCounters(
    doctorId: number
  ): Promise<GetAmbulatoryCounters.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetAmbulatoryCounters.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/orders/ambulatorial/counters?doctorId=${doctorId}`
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(
        RepositoryErrors[httpResponse.error! as keyof typeof RepositoryErrors]
      )
    } else {
      return httpResponse.body as GetAmbulatoryCounters.Model
    }
  }

  async getAmbulatoryOrders(
    params: GetAmbulatoryOrders.Params
  ): Promise<GetAmbulatoryOrders.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetAmbulatoryOrders.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/orders/ambulatorial',
      params: params,
      paramsSerializer: (params: any) => parseParams(params)
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(
        RepositoryErrors[httpResponse.error! as keyof typeof RepositoryErrors]
      )
    } else {
      return httpResponse.body as GetAmbulatoryOrders.Model
    }
  }

  async getOneAmbulatoryOrder(
    params: GetOneAmbulatoryOrder.Params
  ): Promise<GetOneAmbulatoryOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetOneAmbulatoryOrder.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/orders/ambulatorial/${params.id}`
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(
        RepositoryErrors[httpResponse.error! as keyof typeof RepositoryErrors]
      )
    } else {
      return httpResponse.body.data as GetOneAmbulatoryOrder.Model
    }
  }

  async searchAmbulatoryOrders(
    params: SearchAmbulatoryOrders.Params
  ): Promise<SearchAmbulatoryOrders.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<SearchAmbulatoryOrders.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/orders/ambulatorial/doctor/search?search=${params.search}&doctorId=${params.doctorId}`
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(
        RepositoryErrors[httpResponse.error! as keyof typeof RepositoryErrors]
      )
    } else {
      return httpResponse.body as SearchAmbulatoryOrders.Model
    }
  }

  async loadAmbulatoryOrderDocument(
    params: LoadAmbulatoryOrderDocument.Params
  ): Promise<LoadAmbulatoryOrderDocument.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadAmbulatoryOrderDocument.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/orders/documents?orderId=${params.orderId}&documentName=${params.documentName}`
      },
      true
    )

    const contentType = httpResponse.headers['content-type']
    const data = httpResponse.data

    return {
      contentType,
      data
    }
  }

  async getAmbulatoryProcedures(
    params: GetAmbulatoryProcedures.Params
  ): Promise<GetAmbulatoryProcedures.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetAmbulatoryProcedures.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/procedures/search',
      params: params,
      paramsSerializer: (params: any) => parseParams(params)
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError('Ocorreu um erro ao buscar Procedimentos')
    } else {
      return httpResponse.body as GetAmbulatoryProcedures.Model
    }
  }

  async getAmbulatoryCids(
    params: GetAmbulatoryCids.Params
  ): Promise<GetAmbulatoryCids.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetAmbulatoryCids.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/cids/search',
      params: params,
      paramsSerializer: (params: any) => parseParams(params)
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError('Ocorreu um erro ao buscar CIDs')
    } else {
      return httpResponse.body as GetAmbulatoryCids.Model
    }
  }

  async getAmbulatoryMedications(
    params: GetAmbulatoryMedications.Params
  ): Promise<GetAmbulatoryMedications.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<GetAmbulatoryMedications.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/medications/search',
      params: params,
      paramsSerializer: (params: any) => parseParams(params)
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError('Ocorreu um erro ao buscar medicações')
    } else {
      return httpResponse.body as GetAmbulatoryMedications.Model
    }
  }

  async createAmbulatoryOrder(
    params: CreateAmbulatoryOrder.Params
  ): Promise<CreateAmbulatoryOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateAmbulatoryOrder.Model>

    const httpResponse: any = await apiRepository.post({
      url: '/orders/ambulatorial/doctors',
      body: params
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw new CustomError('Ocorreu um erro ao finalizar pedido Ambulatorial')
    } else {
      return httpResponse.body as CreateAmbulatoryOrder.Model
    }
  }
}
